<script lang="ts">
  import type { GrammarError } from '$lib/types/grammar'
  import TextAreaEditor from './TextAreaEditor.svelte'
  import { createTextSegments } from '$lib/utils/textHighlight'
  import { errorFocusStore } from '$lib/stores/errorFocus'

  interface Props {
    value: string
    errors: GrammarError[]
    placeholder?: string
    readonly?: boolean
    disabled?: boolean
    maxLength?: number
    minHeight?: number
    maxHeight?: number
    debounceDelay?: number
    class?: string
    enableMarkdown?: boolean
    textareaElement?: HTMLTextAreaElement | null
    hoveredErrorId?: string | null
    onInput?: (value: string) => void
    onDebouncedInput?: (value: string) => void
    onErrorClick?: (error: GrammarError) => void
    onBackspaceAtStart?: () => void
    focusError?: (error: GrammarError) => void
  }

  let {
    value = $bindable(),
    errors = [],
    placeholder = '',
    readonly = false,
    disabled = false,
    maxLength,
    minHeight,
    maxHeight,
    debounceDelay = 500,
    class: className = '',
    enableMarkdown = true,
    textareaElement = $bindable(null),
    hoveredErrorId = null,
    onInput,
    onDebouncedInput,
    onErrorClick,
    onBackspaceAtStart,
    focusError
  }: Props = $props()

  let textareaEl = $state<HTMLTextAreaElement | null>(null)
  let decoratedEl = $state<HTMLDivElement | null>(null)
  let containerEl = $state<HTMLDivElement | null>(null)
  let focusState = $state({ focusedErrorId: null, source: null, timestamp: 0 })
  let lastScrollTime = 0

  // Sync textarea element to parent and apply inline styles for iframe
  $effect(() => {
    textareaElement = textareaEl

    // Apply inline styles directly to textarea for iframe compatibility
    if (textareaEl) {
      // Force transparent text with inline styles (overrides any CSS)
      textareaEl.style.setProperty('color', 'transparent', 'important')
      textareaEl.style.setProperty('-webkit-text-fill-color', 'transparent', 'important')
      textareaEl.style.setProperty('text-shadow', 'none', 'important')
      textareaEl.style.setProperty('caret-color', 'var(--text-primary, #000)', 'important')

      // Keep the textarea background transparent
      textareaEl.style.setProperty('background', 'transparent', 'important')

      // When errors exist, make text completely invisible
      if (hasErrors) {
        textareaEl.style.setProperty('opacity', '0', 'important')
      } else {
        // Without errors, still keep text transparent but allow interaction
        textareaEl.style.setProperty('opacity', '0.01', 'important')
      }
    }
  })

  // Create segments for text decoration
  let segments = $derived.by(() => {
    if (!value) return []

    // Use the existing createTextSegments utility
    return createTextSegments(value, errors, [], [])
  })

  // Check if we have any errors to show decorations
  let hasErrors = $derived(errors && errors.length > 0)

  // Handle focus error from external component (e.g., error card click)
  $effect(() => {
    if (focusError) {
      // Export the focus function for parent components
      focusError = (error: GrammarError) => {
        if (!textareaEl || !decoratedEl) return

        // Set selection in textarea
        textareaEl.setSelectionRange(error.start, error.end)
        textareaEl.focus()

        // Scroll to the error position
        const errorElement = decoratedEl.querySelector(`[data-error-id="${error.id}"]`)
        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          })
        }

        // Call error click handler
        onErrorClick?.(error)
      }
    }
  })

  // Subscribe to errorFocusStore for card hover events
  $effect(() => {
    const unsubscribe = errorFocusStore.subscribe(state => {
      focusState = state
    })

    return unsubscribe
  })

  // Auto-scroll to error when focused from card hover
  $effect(() => {
    if (focusState.source === 'card' && focusState.focusedErrorId &&
        focusState.timestamp > lastScrollTime && containerEl) {

      const error = errors.find(e => e.id === focusState.focusedErrorId)
      if (!error) {
        lastScrollTime = Date.now()
        return
      }

      // Find the error element in decorated layer
      const errorElement = decoratedEl?.querySelector(`[data-error-id="${focusState.focusedErrorId}"]`) as HTMLElement
      if (errorElement && decoratedEl) {
        // Get the offset of the error element
        const containerRect = containerEl.getBoundingClientRect()
        const errorRect = errorElement.getBoundingClientRect()
        const relativeTop = errorElement.offsetTop

        // Calculate scroll position to center the error
        const targetScroll = relativeTop - (containerRect.height / 2) + (errorRect.height / 2)

        // Set syncing flag to prevent loops
        isScrollSyncing = true

        // Scroll the container
        containerEl.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        })

        // Reset flag after scroll completes
        setTimeout(() => {
          isScrollSyncing = false
        }, 500)
      }

      lastScrollTime = Date.now()
    }
  })

  // Auto-scroll to hovered error (legacy prop-based hover)
  $effect(() => {
    if (hoveredErrorId && decoratedEl) {
      const errorElement = decoratedEl.querySelector(`[data-error-id="${hoveredErrorId}"]`)
      if (errorElement) {
        // Only scroll if the element is not already in view
        const rect = errorElement.getBoundingClientRect()
        const containerRect = decoratedEl.getBoundingClientRect()

        if (rect.top < containerRect.top || rect.bottom > containerRect.bottom) {
          errorElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          })
        }
      }
    }
  })

  let isScrollSyncing = false

  // Sync container scroll to both layers
  function handleContainerScroll() {
    if (isScrollSyncing || !containerEl) return

    isScrollSyncing = true
    requestAnimationFrame(() => {
      if (containerEl && decoratedEl && textareaEl) {
        const scrollTop = containerEl.scrollTop
        const scrollLeft = containerEl.scrollLeft

        decoratedEl.scrollTop = scrollTop
        decoratedEl.scrollLeft = scrollLeft
        textareaEl.scrollTop = scrollTop
        textareaEl.scrollLeft = scrollLeft
      }
      isScrollSyncing = false
    })
  }

  // Get error class for decoration
  function getErrorClass(error: GrammarError | undefined): string {
    if (!error) return ''

    let classes = []

    // Base error class
    switch (error.level) {
      case 'error':
        classes.push('text-decoration-error')
        break
      case 'warning':
        classes.push('text-decoration-warning')
        break
      case 'suggestion':
        classes.push('text-decoration-suggestion')
        break
      default:
        classes.push('text-decoration-error')
    }

    // Add hover class if this error is being hovered
    if (hoveredErrorId && error.id === hoveredErrorId) {
      classes.push('text-decoration-hovered')
    }

    return classes.join(' ')
  }

  // Handle error click
  function handleErrorSpanClick(error: GrammarError | undefined, event: MouseEvent) {
    if (!error || !textareaEl) return

    event.preventDefault()
    event.stopPropagation()

    // Set selection in textarea
    textareaEl.setSelectionRange(error.start, error.end)
    textareaEl.focus()

    // Call error click handler
    onErrorClick?.(error)
  }

  // Sync decorated text styles with textarea
  function syncTextStyles() {
    if (!containerEl || !decoratedEl) return

    // Find the actual textarea element within the container
    const actualTextarea = containerEl.querySelector('textarea')
    if (!actualTextarea) {
      console.warn('Textarea not found for style sync')
      return
    }

    const computedStyle = window.getComputedStyle(actualTextarea)
    const contentEl = decoratedEl.querySelector('.decorated-content') as HTMLElement

    if (contentEl) {
      // Copy all font-related styles directly from textarea (except textDecoration)
      const stylesToCopy = [
        'fontFamily',
        'fontSize',
        'fontWeight',
        'fontStyle',
        'fontVariant',
        'lineHeight',
        'letterSpacing',
        'wordSpacing',
        'textIndent',
        'textAlign',
        'textTransform',
        'textRendering',
        'fontKerning',
        'fontFeatureSettings',
        'fontVariantLigatures'
      ]

      stylesToCopy.forEach(prop => {
        const value = computedStyle[prop as any]
        if (value) {
          (contentEl.style as any)[prop] = value
        }
      })

      // Also copy padding to ensure exact alignment
      contentEl.style.padding = computedStyle.padding
      contentEl.style.paddingTop = computedStyle.paddingTop
      contentEl.style.paddingRight = computedStyle.paddingRight
      contentEl.style.paddingBottom = computedStyle.paddingBottom
      contentEl.style.paddingLeft = computedStyle.paddingLeft

      // Ensure proper text wrapping
      contentEl.style.whiteSpace = 'pre-wrap'
      contentEl.style.wordWrap = 'break-word'
      contentEl.style.overflowWrap = 'break-word'

      // Copy font smoothing settings
      contentEl.style.webkitFontSmoothing = computedStyle.webkitFontSmoothing || 'auto'
      contentEl.style.mozOsxFontSmoothing = computedStyle.mozOsxFontSmoothing || 'auto'

      // Ensure text rendering matches
      contentEl.style.textRendering = computedStyle.textRendering || 'auto'
    }
  }

  // Watch for container and sync styles
  $effect(() => {
    if (containerEl) {
      // Wait for textarea to be rendered
      const checkAndSync = () => {
        const textarea = containerEl.querySelector('textarea')
        if (textarea) {
          syncTextStyles()

          // Watch for font changes via MutationObserver
          const observer = new MutationObserver(() => {
            syncTextStyles()
          })

          observer.observe(textarea, {
            attributes: true,
            attributeFilter: ['style', 'class']
          })

          return observer
        }
        return null
      }

      // Try multiple times to find textarea
      let observer: MutationObserver | null = null
      let attempts = 0
      const interval = setInterval(() => {
        observer = checkAndSync()
        attempts++
        if (observer || attempts > 20) {
          clearInterval(interval)
        }
      }, 100)

      // Re-sync on window resize
      const handleResize = () => syncTextStyles()
      window.addEventListener('resize', handleResize)

      return () => {
        clearInterval(interval)
        window.removeEventListener('resize', handleResize)
        observer?.disconnect()
      }
    }
  })

  // Re-sync when value changes
  $effect(() => {
    value // Watch value
    setTimeout(() => syncTextStyles(), 10)
  })
</script>

<div
  class="text-decoration-container {hasErrors ? 'showing-errors' : ''}"
  bind:this={containerEl}
  onscroll={handleContainerScroll}
>
  <!-- Decorated text layer (visible) -->
  <div
    class="decorated-text-layer"
    bind:this={decoratedEl}
  >
    <div class="decorated-content">
      {#each segments as segment, index}
        {#if segment.error}
          <span
            class="text-segment {getErrorClass(segment.error)}"
            data-error-id={segment.error.id}
            onmouseenter={() => {
              if (segment.error?.id) {
                errorFocusStore.focusFromEditor(segment.error.id)
              }
            }}
            onmouseleave={() => {
              errorFocusStore.clearFocus()
            }}
            onclick={(e) => handleErrorSpanClick(segment.error, e)}
            onkeydown={(e) => {
              if (segment.error && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault()
                handleErrorSpanClick(segment.error, e)
              }
            }}
            role="button"
            tabindex="-1"
            title={segment.error.message || '클릭하여 수정'}
          >
            {segment.text}
          </span>
        {:else}
          <span class="text-segment">{segment.text}</span>
        {/if}
      {/each}

      {#if !value && placeholder}
        <span class="placeholder-text">{placeholder}</span>
      {/if}
    </div>
  </div>

  <!-- Actual textarea (transparent, for input) -->
  <TextAreaEditor
    bind:value
    bind:textareaElement={textareaEl}
    {placeholder}
    {readonly}
    {disabled}
    {maxLength}
    {minHeight}
    {maxHeight}
    {debounceDelay}
    {enableMarkdown}
    {onBackspaceAtStart}
    class="transparent-textarea {className}"
    {onInput}
    {onDebouncedInput}
  />
</div>

<style>
  .text-decoration-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    scroll-behavior: smooth;
    will-change: scroll-position;
  }

  .decorated-text-layer {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: visible;
    pointer-events: none;
    z-index: 1;
    margin: 0;
    transform: none;
  }

  .decorated-content {
    /* Padding will be copied from textarea via JavaScript */
    min-height: 100%;
    box-sizing: border-box;
    color: var(--text-primary);
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    /* Ensure crisp text rendering */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    /* Prevent any blurriness from transforms */
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    /* All other styles will be copied from textarea via JavaScript */
  }

  .text-segment {
    pointer-events: auto;
    cursor: text;
    /* Remove all styling to prevent font changes */
    display: inline;
    /* Prevent any spacing between spans */
    margin: 0;
    padding: 0;
    border: none;
    contain: style;
  }

  /* Text decoration styles for errors - use native CSS wavy underline */
  .text-decoration-error {
    cursor: pointer;
    text-decoration: underline wavy #ef4444;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }

  .text-decoration-warning {
    cursor: pointer;
    text-decoration: underline wavy #f59e0b;
    text-decoration-thickness: 2px;
    text-underline-offset: 3px;
  }

  .text-decoration-suggestion {
    cursor: pointer;
    text-decoration: underline dotted #3b82f6;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
  }

  /* Hover effects - use opacity change instead of background-color */
  .text-decoration-error:hover,
  .text-decoration-warning:hover,
  .text-decoration-suggestion:hover {
    opacity: 0.8;
    will-change: opacity;
  }

  /* Highlight effect when error is hovered from error card */
  .text-decoration-hovered {
    background-color: rgba(239, 68, 68, 0.2) !important;
    animation: pulse 1.5s ease-in-out infinite;
    text-decoration-thickness: 3px !important;
  }

  @keyframes pulse {
    0%, 100% {
      background-color: rgba(239, 68, 68, 0.2);
    }
    50% {
      background-color: rgba(239, 68, 68, 0.35);
    }
  }

  /* Placeholder text */
  .placeholder-text {
    color: var(--text-tertiary);
    pointer-events: none;
  }

  /* Transparent textarea for input */
  :global(.text-decoration-container .transparent-textarea textarea) {
    position: relative;
    z-index: 2;
    /* Completely prevent text rendering while maintaining functionality */
    color: transparent !important;
    -webkit-text-fill-color: transparent !important;
    text-shadow: none !important;
    /* Additional measures to prevent any text rendering artifacts */
    font-size: inherit !important; /* Keep font-size for proper cursor positioning */
    text-rendering: optimizeSpeed !important; /* Disable anti-aliasing */
    -webkit-font-smoothing: none !important;
    -moz-osx-font-smoothing: unset !important;
    /* Hide text using multiple methods to ensure compatibility */
    opacity: 0.01 !important; /* Near-zero opacity prevents rendering but keeps selection */
    filter: alpha(opacity=1) !important; /* IE compatibility */
    /* Maintain interaction */
    caret-color: var(--text-primary);
    background: transparent !important;
    /* Ensure exact positioning */
    margin: 0 !important;
    transform: none !important;
  }

  /* Selection highlight */
  :global(.text-decoration-container .transparent-textarea textarea::selection) {
    background: rgba(99, 102, 241, 0.3);
    color: transparent;
  }

  /* When showing errors, make textarea text completely invisible */
  :global(.text-decoration-container.showing-errors .transparent-textarea textarea) {
    /* Use clip to hide text without affecting layout */
    color: transparent !important;
    text-fill-color: transparent !important;
    -webkit-text-fill-color: transparent !important;
    /* Remove any possibility of text artifacts */
    text-shadow: none !important;
    opacity: 0 !important; /* Complete invisibility */
    /* But maintain caret visibility */
    caret-color: var(--text-primary) !important;
  }

  /* Dark mode adjustments - white underlines with different thickness */
  :global(.dark-theme) .text-decoration-error {
    text-decoration-color: #ffffff;
    text-decoration-thickness: 2px;
  }

  :global(.dark-theme) .text-decoration-warning {
    text-decoration-color: #ffffff;
    text-decoration-thickness: 1.5px;
  }

  :global(.dark-theme) .text-decoration-suggestion {
    text-decoration-color: #ffffff;
    text-decoration-thickness: 1px;
    opacity: 0.7;
  }

  :global(.dark-theme) .text-decoration-hovered {
    background-color: rgba(255, 255, 255, 0.85) !important;
    color: #000000 !important;
    animation: pulse-dark 1.5s ease-in-out infinite;
  }

  /* Auto mode - system dark theme support */
  @media (prefers-color-scheme: dark) {
    .text-decoration-error {
      text-decoration-color: #ffffff;
      text-decoration-thickness: 2px;
    }

    .text-decoration-warning {
      text-decoration-color: #ffffff;
      text-decoration-thickness: 1.5px;
    }

    .text-decoration-suggestion {
      text-decoration-color: #ffffff;
      text-decoration-thickness: 1px;
      opacity: 0.7;
    }

    .text-decoration-hovered {
      background-color: rgba(255, 255, 255, 0.85) !important;
      color: #000000 !important;
      animation: pulse-dark 1.5s ease-in-out infinite;
    }
  }

  @keyframes pulse-dark {
    0%, 100% {
      background-color: rgba(255, 255, 255, 0.85);
    }
    50% {
      background-color: rgba(255, 255, 255, 0.95);
    }
  }
</style>